import { useCallback, useState } from "react";

export interface DialogState<T> {
  value: T | null;
  isOpen: boolean;
  loading: boolean;
  open: (value: T) => void;
  close: () => void;
  setLoading: (loading: boolean) => void;
}

/** Manages open/close state for a dialog that carries a typed value — e.g. the item a detail dialog
 *  is showing, or a row to confirm deletion of. Includes a loading flag for async operations.
 *  close() resets loading so there is no stale state on the next open.
 *  Use useOpenState when the dialog carries no value; useDialogState when it receives data. */
export function useDialogState<T>(): DialogState<T> {
  const [value, setValue] = useState<T | null>(null);
  const [loading, setLoadingState] = useState(false);
  const open = useCallback((v: T) => setValue(v), []);
  const close = useCallback(() => {
    setValue(null);
    setLoadingState(false);
  }, []);
  const setLoading = useCallback((l: boolean) => setLoadingState(l), []);
  return { value, isOpen: value !== null, loading, open, close, setLoading };
}
